import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { timeAgo } from "@/lib/format";
import {
  ShieldCheck, Lock, Inbox, Building2, ListChecks, CreditCard, X, Plus, ArrowRight,
  CheckCircle2, StickyNote, Mail, ChevronRight, Eye, KeyRound, Copy, Users, AlertTriangle, Ship, Send, Pencil,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const LEAD_STATUS_STYLE = {
  New: "#27384A", Contacted: "#3F566E", "Demo Scheduled": "#3F566E", "Pilot Offered": "#3F566E",
  Converted: "#3F566E", "Not a Fit": "#7B8794", Archived: "#A6AEB6",
};
const ACCT_STYLE = { Trial: "#3F566E", Active: "#3F566E", Paused: "#7B8794", Cancelled: "#3F566E" };
const field = "h-9 w-full px-3 rounded-sm border border-line bg-surface text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand";

const TABS = [
  { key: "leads", label: "Leads", icon: Inbox },
  { key: "companies", label: "Companies", icon: Building2 },
  { key: "onboarding", label: "Onboarding", icon: ListChecks },
  { key: "outbox", label: "📨 OUTBOX REVIEW", icon: Send },
];

const DRAFT_STATUS_STYLE = {
  pending: { fg: "#D97706", bg: "#FFFBEB" },
  sent: { fg: "#059669", bg: "#ECFDF5" },
  failed: { fg: "#DC2626", bg: "#FEF2F2" },
};

function buildOnboardingMessage(creds) {
  const firstName = (creds?.name || "").trim().split(/\s+/)[0] || "there";
  const loginUrl = `${window.location.origin}/login`;
  const subject = "Welcome to MovaRaBoard";
  const body = `Hi ${firstName},

Your MovaRaBoard workspace has been created.

Workspace:
${creds?.company || "—"}

Login:
${loginUrl}

Email:
${creds?.email || "—"}

Temporary password:
${creds?.password || "—"}

Please sign in and update your password after your first login.

MovaRaBoard will help your team see active shipment jobs, blockers, missing documents, customer updates, and next actions in one clear workspace.

Best,
MovaRaBoard`;
  return { subject, body, full: `Subject: ${subject}\n\n${body}` };
}

function Pill({ value, color }) {
  return <span className="text-[11px] px-2 py-0.5 rounded-sm font-medium" style={{ color, backgroundColor: `${color}1A` }}>{value}</span>;
}

function NotesBlock({ notes, onAdd, testid }) {
  const [text, setText] = useState("");
  return (
    <div>
      <p className="op-label mb-2 flex items-center gap-1.5"><StickyNote className="h-3.5 w-3.5" /> Internal notes</p>
      <div className="space-y-2 mb-3 max-h-44 overflow-y-auto mv-scroll">
        {(notes || []).length === 0 && <p className="text-[12.5px] text-ink-3">No notes yet.</p>}
        {(notes || []).map((n) => (
          <div key={n.id} className="border border-line rounded-sm p-2.5 bg-surface-alt">
            <p className="text-[12.5px] text-ink whitespace-pre-wrap">{n.text}</p>
            <p className="text-[11px] text-ink-3 font-mono-data mt-1">{n.created_by} · {timeAgo(n.created_at)}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid={`${testid}-note-input`} className={field} placeholder="Add a note…" value={text}
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && text.trim()) { onAdd(text); setText(""); } }} />
        <button data-testid={`${testid}-note-add`} onClick={() => { if (text.trim()) { onAdd(text); setText(""); } }}
          className="h-9 px-3 rounded-sm bg-brand text-white text-[13px] font-medium hover:bg-brand-dark shrink-0">Add</button>
      </div>
    </div>
  );
}

export default function AdminConsole() {
  const { user, loading } = useAuth();
  const { enterWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [meta, setMeta] = useState(null);
  const [openLead, setOpenLead] = useState(null);
  const [openCompany, setOpenCompany] = useState(null);
  const [newCo, setNewCo] = useState(false);
  const [creds, setCreds] = useState(null);
  const [invite, setInvite] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyInvites, setCompanyInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("admin");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [coForm, setCoForm] = useState({ name: "", main_contact: "", company_type: "", plan: "", admin_name: "", admin_email: "" });
  const [drafts, setDrafts] = useState([]);
  const [openDraft, setOpenDraft] = useState(null);
  const [draftForm, setDraftForm] = useState({ subject: "", body: "" });
  const [sendingId, setSendingId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const isSuper = user?.role === "super_admin";
  const loadLeads = () => client.get("/admin/leads").then((r) => setLeads(r.data));
  const loadCompanies = () => client.get("/admin/companies").then((r) => setCompanies(r.data));
  const loadDrafts = () => client.get("/admin/email-drafts").then((r) => setDrafts(r.data)).catch(() => setDrafts([]));

  useEffect(() => {
    if (!isSuper) return;
    loadLeads(); loadCompanies();
    // Backfill drafts for any existing leads, then load the outbox.
    client.post("/admin/email-drafts/backfill").then(loadDrafts).catch(loadDrafts);
    client.get("/admin/meta").then((r) => setMeta(r.data));
  }, [isSuper]);

  useEffect(() => {
    if (openCompany?.id) { loadCompanyUsers(openCompany.id); loadCompanyInvites(openCompany.id); }
    else { setCompanyUsers([]); setCompanyInvites([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCompany?.id]);

  if (loading || !user) {
    return (
      <Layout>
        <div data-testid="admin-loading" className="bg-surface border border-line rounded-md p-12 text-center text-sm text-ink-3">Loading…</div>
      </Layout>
    );
  }

  if (user && !isSuper) {
    return (
      <Layout>
        <div data-testid="admin-restricted" className="bg-surface border border-line rounded-md p-12 text-center">
          <Lock className="h-6 w-6 text-ink-3 mx-auto mb-3" />
          <h2 className="font-heading text-lg font-medium text-ink">Admin Console is restricted</h2>
          <p className="text-sm text-ink-2 mt-1">Only platform Super Admins can access this area.</p>
        </div>
      </Layout>
    );
  }

  const updateLead = async (id, status) => {
    const r = await client.patch(`/admin/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? r.data : l)));
    if (openLead?.id === id) setOpenLead(r.data);
    toast.success(`Lead set to ${status}`);
  };
  const addLeadNote = async (id, text) => {
    const r = await client.post(`/admin/leads/${id}/notes`, { text });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, notes: [...(l.notes || []), r.data] } : l)));
    setOpenLead((p) => (p && p.id === id ? { ...p, notes: [...(p.notes || []), r.data] } : p));
  };
  const convertLead = async (id) => {
    const r = await client.post(`/admin/leads/${id}/convert`);
    await loadLeads(); await loadCompanies();
    setOpenLead(null); setTab("companies");
    const prov = r.data?.provision;
    if (prov?.invited) {
      setInvite({ company: r.data.name, email: prov.admin_email, accept_url: prov.accept_url, email_sent: prov.email_sent, token: prov.token });
    } else {
      toast.success(prov?.note || "Lead converted to company workspace");
    }
  };
  const updateCompany = async (id, changes) => {
    const r = await client.put(`/admin/companies/${id}`, changes);
    setCompanies((cs) => cs.map((c) => (c.id === id ? r.data : c)));
    if (openCompany?.id === id) setOpenCompany(r.data);
  };
  const toggleOnboarding = async (id, item, value) => {
    const r = await client.patch(`/admin/companies/${id}/onboarding`, { item, value });
    setCompanies((cs) => cs.map((c) => (c.id === id ? r.data : c)));
    if (openCompany?.id === id) setOpenCompany(r.data);
  };
  const addCompanyNote = async (id, text) => {
    const r = await client.post(`/admin/companies/${id}/notes`, { text });
    setCompanies((cs) => cs.map((c) => (c.id === id ? { ...c, notes: [...(c.notes || []), r.data] } : c)));
    setOpenCompany((p) => (p && p.id === id ? { ...p, notes: [...(p.notes || []), r.data] } : p));
  };
  const createCompany = async () => {
    if (!coForm.name.trim()) return toast.error("Company name is required.");
    const r = await client.post("/admin/companies", coForm);
    await loadCompanies();
    setNewCo(false); setCoForm({ name: "", main_contact: "", company_type: "", plan: "", admin_name: "", admin_email: "" });
    const prov = r.data?.provision;
    if (prov?.invited) {
      setInvite({ company: r.data.name, email: prov.admin_email, accept_url: prov.accept_url, email_sent: prov.email_sent, token: prov.token });
    } else {
      toast.success(prov?.note || "Company created");
    }
  };
  const resendInvite = async () => {
    if (!invite?.token) return;
    try {
      const r = await client.post(`/admin/invitations/${invite.token}/resend`);
      setInvite({ company: r.data.company_name, email: r.data.email, accept_url: r.data.accept_url, email_sent: r.data.email_sent, token: r.data.token });
      toast.success(r.data.email_sent ? "Invite re-sent by email" : "New invite link generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resend invite");
    }
  };
  const enterCompanyWorkspace = (c) => {
    enterWorkspace(c.id, c.name, c.region, c.enable_interisland_transit);
    navigate("/app/today");
  };
  const loadCompanyUsers = (cid) => client.get(`/admin/companies/${cid}/users`).then((r) => setCompanyUsers(r.data)).catch(() => setCompanyUsers([]));
  const loadCompanyInvites = (cid) => client.get(`/admin/companies/${cid}/invitations`).then((r) => setCompanyInvites(r.data)).catch(() => setCompanyInvites([]));
  const sendCompanyInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviteBusy(true);
    try {
      const r = await client.post(`/admin/companies/${openCompany.id}/invite`, { email, role: inviteRole, origin: window.location.origin });
      setInviteEmail("");
      loadCompanyInvites(openCompany.id);
      setInvite({ company: openCompany.name, email: r.data.email, accept_url: r.data.accept_url, email_sent: r.data.email_sent, token: r.data.token });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send invite");
    } finally {
      setInviteBusy(false);
    }
  };
  const resendCompanyInvite = async (token) => {
    try {
      const r = await client.post(`/admin/invitations/${token}/resend`);
      loadCompanyInvites(openCompany.id);
      setInvite({ company: r.data.company_name, email: r.data.email, accept_url: r.data.accept_url, email_sent: r.data.email_sent, token: r.data.token });
      toast.success(r.data.email_sent ? "Invite re-sent by email" : "New invite link generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resend invite");
    }
  };
  const revokeCompanyInvite = async (token) => {
    try {
      await client.post(`/admin/invitations/${token}/revoke`);
      loadCompanyInvites(openCompany.id);
      toast.success("Invite revoked");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not revoke invite");
    }
  };
  const regeneratePassword = async (cid, u) => {
    const r = await client.post(`/admin/companies/${cid}/users/${u.id}/regenerate-password`);
    setCreds({ company: openCompany?.name, name: r.data.name, email: r.data.email, password: r.data.temp_password, regenerated: true });
  };

  const onbProgress = (c) => {
    const items = Object.values(c.onboarding_checklist || {});
    return `${items.filter(Boolean).length}/${items.length}`;
  };

  const openEditDraft = (d) => {
    setOpenDraft(d);
    setDraftForm({ subject: d.subject || "", body: d.body || "" });
  };
  const saveDraft = async () => {
    if (!openDraft) return;
    setSavingDraft(true);
    try {
      const r = await client.patch(`/admin/email-drafts/${openDraft.id}`, draftForm);
      setDrafts((ds) => ds.map((d) => (d.id === openDraft.id ? r.data : d)));
      setOpenDraft(null);
      toast.success("Draft updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save draft");
    } finally {
      setSavingDraft(false);
    }
  };
  const sendDraft = async (id) => {
    setSendingId(id);
    try {
      const r = await client.post(`/admin/email-drafts/${id}/send`);
      setDrafts((ds) => ds.map((d) => (d.id === id ? r.data : d)));
      toast.success("Email sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email");
      loadDrafts();
    } finally {
      setSendingId(null);
    }
  };
  const regeneratePendingDrafts = async () => {
    if (!window.confirm("Reset all PENDING drafts to the latest pitch template? Any manual edits to pending drafts will be overwritten. Sent emails are not affected.")) return;
    try {
      const r = await client.post("/admin/email-drafts/regenerate-pending");
      await loadDrafts();
      toast.success(`${r.data.updated} draft${r.data.updated === 1 ? "" : "s"} reset to latest template`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset drafts");
    }
  };

  return (
    <Layout>
      <div className="mb-5">
        <p className="op-label mb-1">Platform · Phase 1 (single workspace · tenant isolation = Phase 2)</p>
        <h1 className="font-heading text-2xl font-semibold text-ink flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-steel" strokeWidth={2} /> Admin Console
        </h1>
        <p className="text-sm text-ink-2 mt-1">Manage leads, client company workspaces, onboarding, and plans.</p>
      </div>

      <div className="flex gap-1.5 mb-5 border-b border-line overflow-x-auto mv-scroll">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
            className={`h-10 px-4 text-[13px] font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${tab === key ? "border-brand text-brand" : "border-transparent text-ink-2 hover:text-ink"}`}>
            <Icon className="h-4 w-4" /> {label}
            {key === "leads" && <span className="font-mono-data text-[10.5px] text-ink-3">{leads.length}</span>}
            {key === "companies" && <span className="font-mono-data text-[10.5px] text-ink-3">{companies.length}</span>}
            {key === "outbox" && drafts.filter((d) => d.status === "pending").length > 0 && (
              <span className="font-black text-[10px] text-white bg-[#D97706] px-1.5 py-0.5 rounded-md">{drafts.filter((d) => d.status === "pending").length}</span>
            )}
          </button>
        ))}
      </div>

      {/* LEADS */}
      {tab === "leads" && (
        <div className="bg-surface border border-line rounded-md overflow-hidden">
          <div className="overflow-x-auto mv-scroll">
            <table className="w-full text-left">
              <thead><tr className="border-b border-line bg-surface-alt">
                {["Name", "Company", "Type", "Volume", "Submitted", "Status", ""].map((h) => <th key={h} className="op-label px-4 py-2.5 whitespace-nowrap font-semibold">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-line">
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setOpenLead(l)} className="hover:bg-surface-alt cursor-pointer group">
                    <td className="px-4 py-3"><div className="text-[13.5px] text-ink font-medium">{l.full_name}</div><div className="text-[11.5px] text-ink-3 flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</div></td>
                    <td className="px-4 py-3 text-[13px] text-ink-2 whitespace-nowrap">{l.company_name || "—"}</td>
                    <td className="px-4 py-3 text-[12.5px] text-ink-3 whitespace-nowrap">{l.company_type || "—"}</td>
                    <td className="px-4 py-3 text-[12.5px] text-ink-3 whitespace-nowrap">{l.shipment_volume || "—"}</td>
                    <td className="px-4 py-3 text-[12.5px] text-ink-3 whitespace-nowrap">{timeAgo(l.created_at)}</td>
                    <td className="px-4 py-3"><Pill value={l.status || "New"} color={LEAD_STATUS_STYLE[l.status || "New"]} /></td>
                    <td className="px-4 py-3"><ChevronRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100" /></td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-ink-3">No leads yet. Request-access submissions appear here.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* COMPANIES */}
      {tab === "companies" && (
        <div>
          <div className="flex justify-end mb-3">
            <button data-testid="admin-new-company" onClick={() => setNewCo(true)} className="h-9 px-3.5 rounded-sm bg-brand text-white text-[13px] font-medium flex items-center gap-1.5 hover:bg-brand-dark"><Plus className="h-4 w-4" /> Create Company</button>
          </div>
          <div className="bg-surface border border-line rounded-md overflow-hidden">
            <div className="overflow-x-auto mv-scroll">
              <table className="w-full text-left">
                <thead><tr className="border-b border-line bg-surface-alt">
                  {["Company", "Contact", "Type", "Plan", "Status", "Onboarding", "Created", ""].map((h) => <th key={h} className="op-label px-4 py-2.5 whitespace-nowrap font-semibold">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-line">
                  {companies.map((c) => (
                    <tr key={c.id} data-testid={`company-row-${c.id}`} onClick={() => setOpenCompany(c)} className="hover:bg-surface-alt cursor-pointer group">
                      <td className="px-4 py-3 text-[13.5px] text-ink font-medium whitespace-nowrap">{c.name}</td>
                      <td className="px-4 py-3 text-[13px] text-ink-2 whitespace-nowrap">{c.main_contact || "—"}</td>
                      <td className="px-4 py-3 text-[12.5px] text-ink-3 whitespace-nowrap">{c.company_type || "—"}</td>
                      <td className="px-4 py-3 text-[12.5px] text-ink-2 whitespace-nowrap">{c.plan || "—"}</td>
                      <td className="px-4 py-3"><Pill value={c.account_status} color={ACCT_STYLE[c.account_status]} /></td>
                      <td className="px-4 py-3 font-mono-data text-[12px] text-ink-2 whitespace-nowrap">{onbProgress(c)}</td>
                      <td className="px-4 py-3 text-[12.5px] text-ink-3 whitespace-nowrap">{timeAgo(c.created_at)}</td>
                      <td className="px-4 py-3"><ChevronRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100" /></td>
                    </tr>
                  ))}
                  {companies.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-ink-3">No companies yet. Convert a lead or create one.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ONBOARDING */}
      {tab === "onboarding" && (
        <div className="space-y-4">
          {companies.length === 0 && <div className="bg-surface border border-line rounded-md p-12 text-center text-sm text-ink-3">No companies to onboard yet.</div>}
          {companies.map((c) => (
            <div key={c.id} data-testid={`onboarding-${c.id}`} className="bg-surface border border-line rounded-md p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div><h3 className="font-heading text-[15px] font-semibold text-ink">{c.name}</h3><span className="font-mono-data text-[11.5px] text-ink-3">{onbProgress(c)} complete</span></div>
                {meta && (
                  <select data-testid={`onboarding-status-${c.id}`} className={`${field} w-auto`} value={c.onboarding_status} onChange={(e) => updateCompany(c.id, { onboarding_status: e.target.value })}>
                    {meta.onboarding_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {meta?.onboarding_items.map((it) => {
                  const done = c.onboarding_checklist?.[it.key];
                  return (
                    <button key={it.key} data-testid={`onb-item-${c.id}-${it.key}`} onClick={() => toggleOnboarding(c.id, it.key, !done)}
                      className={`flex items-center gap-2.5 px-3 h-11 rounded-sm border text-[12.5px] text-left transition-colors ${done ? "border-ok/40 bg-ok/[0.06] text-ink" : "border-line bg-surface text-ink-2 hover:border-steel"}`}>
                      {done ? <CheckCircle2 className="h-4 w-4 text-ok shrink-0" /> : <span className="h-4 w-4 rounded-[3px] border border-line shrink-0" />}
                      {it.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* OUTBOX REVIEW */}
      {tab === "outbox" && (
        <div className="bg-surface border-2 border-[#0B132B] rounded-md overflow-hidden shadow-[2px_2px_0px_0px_#0B132B]" data-testid="outbox-panel">
          <div className="bg-[#0B132B] px-4 py-2.5 flex items-center gap-2">
            <Send className="h-4 w-4 text-[#D97706]" strokeWidth={2} />
            <span className="text-white font-black text-[13px] tracking-wide uppercase">Outbound Review Queue</span>
            <button data-testid="regenerate-drafts-btn" onClick={regeneratePendingDrafts}
              className="ml-auto text-[11px] text-white/70 hover:text-white border border-white/30 hover:border-white/60 rounded-sm px-2 py-1 transition-colors flex items-center gap-1">
              ↻ Reset pending to latest template
            </button>
            <span className="text-[11px] text-white/50 font-mono-data hidden sm:inline">reply-to: {drafts[0]?.reply_to || "hello@movaraboard.com"}</span>
          </div>
          <div className="overflow-x-auto mv-scroll">
            <table className="w-full text-left">
              <thead><tr className="border-b-2 border-[#0B132B] bg-surface-alt">
                {["Recipient", "Company", "Subject", "Status", "Actions"].map((h) => <th key={h} className="op-label px-4 py-2.5 whitespace-nowrap font-black">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-line">
                {drafts.map((d) => {
                  const st = DRAFT_STATUS_STYLE[d.status] || DRAFT_STATUS_STYLE.pending;
                  const sent = d.status === "sent";
                  return (
                    <tr key={d.id} data-testid={`draft-row-${d.id}`} className="hover:bg-surface-alt">
                      <td className="px-4 py-3">
                        <div className="text-[13.5px] text-ink font-medium whitespace-nowrap">{d.recipient_name || "—"}</div>
                        <div className="text-[11.5px] text-ink-3 flex items-center gap-1"><Mail className="h-3 w-3" />{d.recipient_email}</div>
                      </td>
                      <td className="px-4 py-3 text-[12.5px] text-ink-2 whitespace-nowrap">{d.company_name || "—"}</td>
                      <td className="px-4 py-3 text-[12.5px] text-ink max-w-[240px] truncate">{d.subject}</td>
                      <td className="px-4 py-3">
                        <span data-testid={`draft-status-${d.id}`} className="inline-flex items-center rounded-sm font-black border-2 px-2 py-0.5 text-[10.5px] uppercase tracking-wide"
                          style={{ color: st.fg, backgroundColor: st.bg, borderColor: st.fg }}>{d.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button data-testid={`draft-edit-${d.id}`} onClick={() => openEditDraft(d)} disabled={sent}
                            className="h-9 px-3 rounded-md bg-white text-[#0B132B] text-[12.5px] font-bold flex items-center gap-1.5 border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] hover:bg-[#F8FAFC] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none">
                            <Pencil className="h-3.5 w-3.5" /> Edit Text
                          </button>
                          <button data-testid={`draft-send-${d.id}`} onClick={() => sendDraft(d.id)} disabled={sent || sendingId === d.id}
                            className="text-white font-black text-[12.5px] px-4 py-2 rounded-md border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-1.5"
                            style={{ backgroundColor: sent ? "#059669" : "#D97706" }}>
                            {sent ? "✓ SENT" : sendingId === d.id ? "SENDING…" : "🚀 SEND NOW"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {drafts.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-ink-3">No lead drafts yet. New request-access submissions generate a pitch draft here automatically.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EDIT DRAFT MODAL */}
      <Dialog open={!!openDraft} onOpenChange={(o) => !o && setOpenDraft(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg rounded-md max-h-[90vh] !flex flex-col p-0 gap-0" data-testid="draft-edit-modal">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="font-heading text-[#0B132B] font-black flex items-center gap-2"><Pencil className="h-4 w-4" /> Edit Text</DialogTitle>
          </DialogHeader>
          {openDraft && (
            <form onSubmit={(e) => { e.preventDefault(); saveDraft(); }} className="flex flex-col min-h-0 flex-1">
              <div className="px-6 py-3 overflow-y-auto flex-1 min-h-0 space-y-3.5">
                <div className="text-[12.5px] text-ink-2 border border-line rounded-sm bg-surface-alt/50 px-3 py-2">
                  To <span className="font-medium text-ink">{openDraft.recipient_name}</span> &lt;{openDraft.recipient_email}&gt;
                </div>
                <div>
                  <label className="op-label block mb-1.5">Subject</label>
                  <input data-testid="draft-subject-input" enterKeyHint="next" className={field} value={draftForm.subject} onChange={(e) => setDraftForm((f) => ({ ...f, subject: e.target.value }))} />
                </div>
                <div>
                  <label className="op-label block mb-1.5">Message</label>
                  <textarea data-testid="draft-body-input" enterKeyHint="done" rows={12} value={draftForm.body} onChange={(e) => setDraftForm((f) => ({ ...f, body: e.target.value }))}
                    className="w-full px-3 py-2 rounded-sm border border-line bg-surface text-sm text-ink leading-relaxed focus:outline-none focus:ring-1 focus:ring-brand resize-none" />
                </div>
              </div>
              <div className="bg-white border-t-2 border-[#0B132B] p-4 shrink-0 flex gap-2.5">
                <button type="button" onClick={() => setOpenDraft(null)} className="h-11 px-4 rounded-md bg-white text-[#0B132B] font-bold border-2 border-[#0B132B] hover:bg-[#F8FAFC] active:translate-y-0.5 transition-all">Cancel</button>
                <button type="submit" data-testid="draft-save-btn" disabled={savingDraft}
                  className="flex-1 bg-[#D97706] hover:bg-[#B45309] text-white font-black py-3 rounded-md border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all disabled:opacity-60">
                  {savingDraft ? "Saving…" : "Save Draft"}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!openLead} onOpenChange={(o) => !o && setOpenLead(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg rounded-md !flex flex-col max-h-[88vh] overflow-y-auto mv-scroll">
          {openLead && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{openLead.full_name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0 text-[13px]">
                  <div><div className="op-label mb-0.5">Email</div>{openLead.email}</div>
                  <div><div className="op-label mb-0.5">Company</div>{openLead.company_name || "—"}</div>
                  <div><div className="op-label mb-0.5">Role</div>{openLead.role_title || "—"}</div>
                  <div><div className="op-label mb-0.5">Type</div>{openLead.company_type || "—"}</div>
                  <div><div className="op-label mb-0.5">Volume</div>{openLead.shipment_volume || "—"}</div>
                  <div><div className="op-label mb-0.5">Submitted</div>{timeAgo(openLead.created_at)}</div>
                </div>
                {(openLead.improvement_goals || []).length > 0 && (
                  <div><div className="op-label mb-1.5">Goals</div><div className="flex flex-wrap gap-1.5">{openLead.improvement_goals.map((g) => <span key={g} className="text-[11.5px] px-2 py-0.5 rounded-sm bg-surface-alt border border-line text-ink-2">{g}</span>)}</div></div>
                )}
                {openLead.message && <div><div className="op-label mb-1">Message</div><p className="text-[13px] text-ink-2 bg-surface-alt border border-line rounded-sm p-3">{openLead.message}</p></div>}
                <div>
                  <div className="op-label mb-1.5">Status</div>
                  <select data-testid="lead-status-select" className={field} value={openLead.status || "New"} onChange={(e) => updateLead(openLead.id, e.target.value)}>
                    {meta?.lead_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <NotesBlock notes={openLead.notes} onAdd={(t) => addLeadNote(openLead.id, t)} testid="lead" />
              </div>
              <DialogFooter>
                {openLead.status !== "Converted" && (
                  <button data-testid="lead-convert-btn" onClick={() => convertLead(openLead.id)} className="h-9 px-4 rounded-sm bg-brand text-white text-sm font-medium flex items-center gap-1.5 hover:bg-brand-dark">Convert to Company <ArrowRight className="h-4 w-4" /></button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* COMPANY DETAIL */}
      <Dialog open={!!openCompany} onOpenChange={(o) => !o && setOpenCompany(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg rounded-md !flex flex-col max-h-[88vh] overflow-y-auto mv-scroll">
          {openCompany && meta && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{openCompany.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
                  <div><div className="op-label mb-1">Plan</div>
                    <select data-testid="company-plan-select" className={field} value={openCompany.plan || ""} onChange={(e) => updateCompany(openCompany.id, { plan: e.target.value })}>
                      <option value="">No plan</option>
                      {(meta.plan_options || []).map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div><div className="op-label mb-1">Account status</div>
                    <select data-testid="company-status-select" className={field} value={openCompany.account_status} onChange={(e) => updateCompany(openCompany.id, { account_status: e.target.value })}>
                      {meta.account_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><div className="op-label mb-0.5">Main contact</div><div className="text-[13px] text-ink">{openCompany.main_contact || "—"}</div></div>
                  <div><div className="op-label mb-0.5">Type</div><div className="text-[13px] text-ink">{openCompany.company_type || "—"}</div></div>
                  <div><div className="op-label mb-0.5">Active users</div><div className="font-mono-data text-[13px] text-ink">{openCompany.active_users}</div></div>
                  <div><div className="op-label mb-0.5">Active jobs</div><div className="font-mono-data text-[13px] text-ink">{openCompany.active_jobs}</div></div>
                </div>

                {/* REGIONAL FEATURE GATING */}
                <div className="border border-line rounded-md p-3 bg-surface-alt/40" data-testid="company-region-block">
                  <div className="op-label mb-2.5 flex items-center gap-1.5"><Ship className="h-3.5 w-3.5" /> Regional configuration</div>
                  <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0 items-end">
                    <div><div className="op-label mb-1">Operating region</div>
                      <select data-testid="company-region-select" className={field} value={openCompany.region || "US_OVERLAND"} onChange={(e) => updateCompany(openCompany.id, { region: e.target.value })}>
                        {(meta.region_options || ["US_OVERLAND", "CARIBBEAN_MARITIME"]).map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                      </select></div>
                    <label className="flex items-center gap-2.5 h-9 px-3 rounded-sm border border-line bg-surface cursor-pointer">
                      <input data-testid="company-interisland-toggle" type="checkbox" checked={!!openCompany.enable_interisland_transit}
                        onChange={(e) => updateCompany(openCompany.id, { enable_interisland_transit: e.target.checked })}
                        className="h-4 w-4 accent-[#059669]" />
                      <span className="text-[13px] text-ink font-medium">Enable Inter-Island Transit module</span>
                    </label>
                  </div>
                  <p className="text-[11px] text-ink-3 mt-2">Enabling this reveals the "Inter-Island Ports" sidebar module and vessel tracking fields for this workspace.</p>
                </div>

                {/* INTERNAL BILLING (admin-only tracking; no automation) */}
                <div className="border border-line rounded-md p-3 bg-surface-alt/40">
                  <div className="op-label mb-2.5 flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Internal billing — admin tracking only</div>
                  <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
                    <div><div className="op-label mb-1">Setup fee ($)</div>
                      <input data-testid="company-setup-fee" type="number" className={field} defaultValue={openCompany.setup_fee ?? ""} onBlur={(e) => updateCompany(openCompany.id, { setup_fee: e.target.value === "" ? 0 : Number(e.target.value) })} /></div>
                    <div><div className="op-label mb-1">Monthly fee ($)</div>
                      <input data-testid="company-monthly-fee" type="number" className={field} defaultValue={openCompany.monthly_fee ?? ""} onBlur={(e) => updateCompany(openCompany.id, { monthly_fee: e.target.value === "" ? 0 : Number(e.target.value) })} /></div>
                    <div><div className="op-label mb-1">Billing status</div>
                      <select data-testid="company-billing-status" className={field} value={openCompany.billing_status || "Not Started"} onChange={(e) => updateCompany(openCompany.id, { billing_status: e.target.value })}>
                        {(meta.billing_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                      </select></div>
                    <div><div className="op-label mb-1">Next billing date</div>
                      <input data-testid="company-next-billing" type="date" className={field} defaultValue={(openCompany.next_billing_date || "").slice(0, 10)} onBlur={(e) => updateCompany(openCompany.id, { next_billing_date: e.target.value })} /></div>
                  </div>
                  <div className="mt-3"><div className="op-label mb-1">Internal billing note</div>
                    <textarea data-testid="company-billing-note" rows={2} className={`${field} resize-none`} defaultValue={openCompany.internal_billing_note || ""} onBlur={(e) => updateCompany(openCompany.id, { internal_billing_note: e.target.value })} placeholder="Private note — not shown to the client" /></div>
                </div>
                <p className="text-[11.5px] text-ink-3 bg-surface-alt border border-line rounded-sm px-3 py-2">{openCompany.id === "movora" ? "Demo workspace — opens in full test/edit mode for platform testing." : "Enter this workspace to add jobs, upload delivery proofs, and edit the client's live data."}</p>
                <button data-testid="enter-workspace-btn" onClick={() => enterCompanyWorkspace(openCompany)}
                  className="w-full h-9 rounded-sm border border-brand text-brand text-[13px] font-medium hover:bg-brand/[0.06] transition-colors flex items-center justify-center gap-1.5">
                  <Eye className="h-4 w-4" /> {openCompany.id === "movora" ? "Enter Workspace (test/edit)" : "Enter Workspace (live edit)"}
                </button>
                <div>
                  <p className="op-label mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Users &amp; admins</p>
                  <div className="space-y-1.5">
                    {companyUsers.length === 0 && <p className="text-[12.5px] text-ink-3">No users yet in this workspace.</p>}
                    {companyUsers.map((u) => (
                      <div key={u.id} data-testid={`company-user-${u.id}`} className="flex items-center gap-2 border border-line rounded-sm px-2.5 py-2 bg-surface-alt">
                        <div className="min-w-0 flex-1">
                          <div className="text-[12.5px] text-ink font-medium truncate">{u.name} <span className="text-ink-3 font-normal">· {u.role}</span></div>
                          <div className="text-[11px] text-ink-3 font-mono-data truncate">{u.email}</div>
                        </div>
                        <button data-testid={`regen-pw-${u.id}`} onClick={() => regeneratePassword(openCompany.id, u)}
                          className="shrink-0 h-7 px-2.5 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel text-[11.5px] font-medium flex items-center gap-1 transition-colors">
                          <KeyRound className="h-3.5 w-3.5" /> Regenerate Temp Password
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <div data-testid="company-invites-section">
                  <p className="op-label mb-2 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Workspace invites</p>
                  <div className="flex gap-2 mb-2.5">
                    <input data-testid="company-invite-email" type="email" inputMode="email" enterKeyHint="send" autoComplete="off"
                      value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && inviteEmail.trim() && !inviteBusy) { e.preventDefault(); sendCompanyInvite(); } }}
                      placeholder="admin@company.com" className={`${field} flex-1`} />
                    <select data-testid="company-invite-role" className={`${field} w-28 shrink-0`} value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="operator">Operator</option>
                    </select>
                    <button data-testid="company-invite-send" onClick={sendCompanyInvite} disabled={inviteBusy || !inviteEmail.trim()}
                      className="shrink-0 h-9 px-3.5 rounded-sm bg-brand text-white text-[12.5px] font-medium hover:bg-brand-dark transition-colors disabled:opacity-60">
                      {inviteBusy ? "Sending…" : "Send invite"}
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {companyInvites.length === 0 && <p className="text-[12.5px] text-ink-3">No invites yet.</p>}
                    {companyInvites.map((inv) => {
                      const st = inv.status === "accepted" ? { t: "Accepted", c: "#059669", b: "#ECFDF5" }
                        : inv.status === "revoked" ? { t: "Revoked", c: "#64748B", b: "#F1F5F9" }
                        : inv.expired ? { t: "Expired", c: "#DC2626", b: "#FEF2F2" }
                        : { t: "Pending", c: "#D97706", b: "#FFFBEB" };
                      const canManage = inv.status === "pending";
                      return (
                        <div key={inv.token} data-testid={`company-invite-${inv.email}`} className="flex items-center gap-2 border border-line rounded-sm px-2.5 py-2 bg-surface-alt">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[12.5px] text-ink font-medium truncate font-mono-data">{inv.email}</span>
                              <span className="shrink-0 text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded border" style={{ color: st.c, backgroundColor: st.b, borderColor: st.c }}>{st.t}</span>
                            </div>
                            <div className="text-[11px] text-ink-3 truncate">{inv.role} · invited by {inv.invited_by || "—"}</div>
                          </div>
                          {canManage && (
                            <div className="flex items-center gap-1 shrink-0">
                              <button data-testid={`invite-copy-${inv.email}`} onClick={() => { navigator.clipboard?.writeText(inv.accept_url); toast.success("Invite link copied"); }}
                                className="h-7 w-7 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel flex items-center justify-center transition-colors" title="Copy invite link"><Copy className="h-3.5 w-3.5" /></button>
                              <button data-testid={`invite-resend-${inv.email}`} onClick={() => resendCompanyInvite(inv.token)}
                                className="h-7 px-2 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel text-[11px] font-medium transition-colors">Resend</button>
                              <button data-testid={`invite-revoke-${inv.email}`} onClick={() => revokeCompanyInvite(inv.token)}
                                className="h-7 px-2 rounded-sm border border-danger/40 text-danger hover:bg-danger/[0.08] text-[11px] font-medium transition-colors">Revoke</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <NotesBlock notes={openCompany.notes} onAdd={(t) => addCompanyNote(openCompany.id, t)} testid="company" />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW COMPANY */}
      <Dialog open={newCo} onOpenChange={setNewCo}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-md !flex flex-col">
          <DialogHeader><DialogTitle className="font-heading">Create Company</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            <div><label className="op-label block mb-1.5">Company name</label><input data-testid="new-company-name" className={field} value={coForm.name} onChange={(e) => setCoForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div><label className="op-label block mb-1.5">Main contact</label><input data-testid="new-company-contact" className={field} value={coForm.main_contact} onChange={(e) => setCoForm((f) => ({ ...f, main_contact: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
              <div><label className="op-label block mb-1.5">Admin name</label><input data-testid="new-company-admin-name" className={field} value={coForm.admin_name} onChange={(e) => setCoForm((f) => ({ ...f, admin_name: e.target.value }))} /></div>
              <div><label className="op-label block mb-1.5">Admin email</label><input data-testid="new-company-admin-email" type="email" className={field} value={coForm.admin_email} onChange={(e) => setCoForm((f) => ({ ...f, admin_email: e.target.value }))} placeholder="admin@company.com" /></div>
            </div>
            <p className="text-[11px] text-ink-3">Provide an admin email and we'll send them a secure invite link to set their own password (valid 48 hours).</p>
            <div><label className="op-label block mb-1.5">Plan</label>
              <select data-testid="new-company-plan" className={field} value={coForm.plan} onChange={(e) => setCoForm((f) => ({ ...f, plan: e.target.value }))}>
                <option value="">No plan</option>{(meta?.plan_options || []).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter><button data-testid="new-company-submit" onClick={createCompany} className="h-9 px-4 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-dark">Create</button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* WORKSPACE ADMIN CREDENTIALS (shown once) */}
      <Dialog open={!!creds} onOpenChange={(o) => !o && setCreds(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-md !flex flex-col max-h-[90vh] overflow-y-auto mv-scroll" data-testid="creds-modal">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><KeyRound className="h-5 w-5 text-ok" /> {creds?.regenerated ? "Temporary password regenerated" : "Workspace admin created"}</DialogTitle></DialogHeader>
          {creds && (
            <div className="space-y-4">
              <p className="text-[13px] text-ink-2">
                {creds.regenerated
                  ? <>A new temporary password was generated for <span className="font-medium text-ink">{creds.email}</span>{creds.company ? <> in <span className="font-medium text-ink">{creds.company}</span></> : null}.</>
                  : <>The first admin for <span className="font-medium text-ink">{creds.company}</span> has been created.</>}
              </p>
              <div className="flex items-start gap-2 bg-warn/[0.1] border border-warn/30 rounded-sm px-3 py-2 text-[12.5px] text-ink-2" data-testid="creds-warning">
                <AlertTriangle className="h-4 w-4 text-warn shrink-0 mt-0.5" />
                <span>This temporary password is shown <span className="font-medium text-ink">once</span>. Copy it before closing.</span>
              </div>
              <div className="space-y-2.5">
                <div>
                  <div className="op-label mb-1">Email</div>
                  <div data-testid="creds-email" className="h-9 px-3 flex items-center rounded-sm border border-line bg-surface-alt text-[13px] font-mono-data text-ink">{creds.email}</div>
                </div>
                <div>
                  <div className="op-label mb-1">Temporary password</div>
                  <div className="flex gap-2">
                    <div data-testid="creds-password" className="h-9 px-3 flex-1 flex items-center rounded-sm border border-line bg-surface-alt text-[13px] font-mono-data text-ink">{creds.password}</div>
                    <button data-testid="creds-copy" onClick={() => { navigator.clipboard?.writeText(`${creds.email} / ${creds.password}`); toast.success("Credentials copied"); }}
                      className="h-9 px-3 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel flex items-center gap-1.5 text-[12.5px]"><Copy className="h-3.5 w-3.5" /> Copy</button>
                  </div>
                </div>
              </div>

              <div className="border-t border-line pt-3.5">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="op-label flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Copy onboarding message</div>
                  <button data-testid="creds-copy-message"
                    onClick={() => { navigator.clipboard?.writeText(buildOnboardingMessage(creds).full); toast.success("Onboarding message copied"); }}
                    className="h-7 px-2.5 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel flex items-center gap-1.5 text-[12px] font-medium"><Copy className="h-3.5 w-3.5" /> Copy message</button>
                </div>
                <pre data-testid="creds-onboarding-message" className="text-[11.5px] leading-relaxed text-ink-2 bg-surface-alt border border-line rounded-sm p-3 max-h-52 overflow-y-auto mv-scroll whitespace-pre-wrap font-body">{`Subject: ${buildOnboardingMessage(creds).subject}\n\n${buildOnboardingMessage(creds).body}`}</pre>
                <p className="text-[11px] text-ink-3 mt-1.5">Email sending will be added later. Copy and send this message manually for now.</p>
              </div>
            </div>
          )}
          <DialogFooter><button data-testid="creds-done" onClick={() => setCreds(null)} className="h-9 px-4 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-dark">Done</button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WORKSPACE INVITE SENT */}
      <Dialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-md !flex flex-col" data-testid="invite-modal">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Mail className="h-5 w-5 text-ok" /> {invite?.email_sent ? "Invite sent" : "Invite created"}</DialogTitle></DialogHeader>
          {invite && (
            <div className="space-y-4">
              <p className="text-[13px] text-ink-2">
                {invite.email_sent
                  ? <>A secure invite was emailed to <span className="font-medium text-ink">{invite.email}</span> for <span className="font-medium text-ink">{invite.company}</span>. They'll set their own password and be taken straight into the workspace.</>
                  : <>The invite for <span className="font-medium text-ink">{invite.email}</span> was created, but the email could not be sent. Share the link below manually.</>}
              </p>
              {!invite.email_sent && (
                <div className="flex items-start gap-2 bg-warn/[0.1] border border-warn/30 rounded-sm px-3 py-2 text-[12.5px] text-ink-2" data-testid="invite-email-warning">
                  <AlertTriangle className="h-4 w-4 text-warn shrink-0 mt-0.5" />
                  <span>Email delivery failed. Copy the invite link and send it yourself.</span>
                </div>
              )}
              <div>
                <div className="op-label mb-1">Invite link (valid 48h)</div>
                <div className="flex gap-2">
                  <div data-testid="invite-link" className="h-9 px-3 flex-1 flex items-center rounded-sm border border-line bg-surface-alt text-[12px] font-mono-data text-ink truncate">{invite.accept_url}</div>
                  <button data-testid="invite-copy-link" onClick={() => { navigator.clipboard?.writeText(invite.accept_url); toast.success("Invite link copied"); }}
                    className="h-9 px-3 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel flex items-center gap-1.5 text-[12.5px] shrink-0"><Copy className="h-3.5 w-3.5" /> Copy</button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button data-testid="invite-resend" onClick={resendInvite} className="h-9 px-4 rounded-sm border border-line text-ink-2 hover:text-ink hover:border-steel text-sm font-medium">Resend invite</button>
            <button data-testid="invite-done" onClick={() => setInvite(null)} className="h-9 px-4 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-dark">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
